import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigationGuard } from '../../../../../hooks/useNavigationGuard';
import musicLoader from '../../../../../assets/images/music-loader.gif';
import loaderBackground from '../../../../../assets/images/Background+Blur.svg';
import { trackNavigationEvent, createTrackingPayload } from '../../../../../helper/navigationTracker';
import { clearUploadData, mixPreviewData, uploadData } from '../../../../../redux/actions/audioEngineer';
import { completeProcess } from '../../../../../redux/actions/backgroundProcesses';

const messages = [
  "Analyzing your music…",
  "Detecting genre…",
  "Detecting sub-genre…",
  "Building custom plugin chain…",
  "Adapting engine to style…",
  "Optimizing stereo field…",
  "Adjusting frequency balance…",
  "Optimizing loudness…",
  "Applying magic touch…",
  "Preparing your preview…",
  "Almost there…"
];

const leaveWarningMessage = "Your processing is still in progress. Leaving this page may interrupt the task.";

const stateToIndexMap = {
  MIX_TASK_PENDING: 1,
  MIX_TASK_STARTED: 2,
  MIX_TASK_ANALYSIS: 4,
  MIX_TASK_APPLY_FX: 7,
  MIX_TASK_PREVIEW_COMPLETED: 10,
};

const ProcessingLoader = ({ isMixed, processState, taskId, uploadId, isDemo = false }) => {
  const dispatch = useDispatch()
  const stepCount = messages.length - 1; // 10

  const [currentIndex, setCurrentIndex] = useState(0);
  const { currentProcessId } = useSelector((state) => state.backgroundProcesses || {});

  const isFailed = processState === "MIX_TASK_PREVIEW_FAILED";
  const isCompletedByEngine = processState === "MIX_TASK_PREVIEW_COMPLETED";
  const isFinalCompleted = isCompletedByEngine;

  // 1. Auto-advance progress until failed or completed
  useEffect(() => {
    if (isFailed || isFinalCompleted) {
      setCurrentIndex(stepCount);
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev < stepCount - 1 ? prev + 1 : prev));
    }, 10000);

    return () => clearInterval(interval);
  }, [isFailed, isFinalCompleted, stepCount]);

  // 2. Block browser back/forward and app back navigation only
  useNavigationGuard(
    !isDemo,
    `${leaveWarningMessage} Do you want to leave?`,
    () => {
      dispatch(completeProcess(currentProcessId))
      dispatch(clearUploadData());
      dispatch(uploadData({}))
      dispatch(mixPreviewData({}))
    }
  );

  // 3. Warn on browser refresh/close/tab close
  useEffect(() => {
    if (isDemo) return;
    const handleBeforeUnload = (event) => {
      const payload = createTrackingPayload('page_refresh', {
        taskId: taskId,
        uploadId: uploadId,
        operationName: isMixed ? 'Mixing Engineer Final' : 'Mixing Engineer Preview',
        errorMessage: `User attempted to refresh/close the page during Mixing Engineer ${isMixed ? 'final mixing' : 'preview'}`,
        metadata: {
          component: 'Mixing Engineer ProcessingLoader',
          isMixed,
        }
      });
      trackNavigationEvent(payload);

      event.preventDefault();
      event.returnValue = leaveWarningMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDemo, isMixed, taskId, uploadId]);

  // whenever external mix completes, restart message cycle from beginning
  useEffect(() => {
    if (isMixed) {
      setCurrentIndex(0);
    }
  }, [isMixed]);

  useEffect(() => {
    if (!processState) return;

    if (isFailed) {
      setCurrentIndex(stepCount);
      return;
    }

    if (isFinalCompleted) {
      setCurrentIndex(stepCount);
      return;
    }

    const milestone = stateToIndexMap[processState];
    if (milestone !== undefined) {
      setCurrentIndex(prev => (milestone > prev ? milestone : prev));
    }
  }, [processState, isFailed, isFinalCompleted, stepCount]);

  const progress = isFailed || isFinalCompleted
    ? 100
    : Math.round((currentIndex / stepCount) * 100);

  const currentMessage = isFailed
    ? "Mix preview failed."
    : messages[currentIndex];

  return (
    <div className="bg-white max-w-[700px] mx-auto flex flex-col items-center justify-center text-center gap-6 rounded-2xl p-4 lg:p-5">

      <div className="relative w-48 h-48 mx-auto">
        {/* <img
          src={loaderBackground}
          className="absolute inset-0 w-full h-full object-contain"
        /> */}
        <img
          src={musicLoader}
          alt="Processing"
          className="absolute inset-0 w-full h-full object-contain"
        />
      </div>

      <div className="w-full max-w-[400px] mx-auto">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-700 ${isFailed ? "bg-red-500" : "bg-[#00067e]"
              }`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="text-[#FA6138] text-base font-medium mt-3">
          {currentMessage}
        </div>
      </div>

      <div className="text-theme_clr2 text-base font-medium opacity-80 leading-relaxed">
        {isMixed
          ? <i>Getting your song ready for release.</i>
          : <i>
            We're analyzing your audio and building a custom<br />
            mixing chain based on its unique genre and tone.
          </i>
        }

      </div>
      <div className="text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        Please do not refresh, close, or navigate away while processing.
      </div>
    </div>
  );
};

export default ProcessingLoader;